import useResizeObserver from '@react-hook/resize-observer';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { NoData } from '@fox/atoms/src/no-data/index';
import { Skeleton } from '@fox/atoms/src/skeleton/index';
import { debounce } from '@fox/atoms/utils/debounce';
import { useContainerHeight } from '@fox/atoms/src/container-context/index';
import { useDebounce } from '@fox/atoms/hooks/use-debounce';
import { useFullScreenContext } from '@fox/atoms/src/full-screen/index';
import { alpha } from '@mui/system';
import { nanoid } from 'nanoid';
import { Options, defaultRangeExtractor, useVirtual } from 'react-virtual';
import styled, { css } from 'styled-components';
import { FoxTableProps } from '../contracts/table';
import { CellItem, CellItemPre, calculateCellBgColours } from './components/cell-item';
import { Cell, Column, HoverRowVisible, RowFiller } from './components/common';
import { DataGridContextProvider, DataGridDataContextProvider } from './context';
import { RowPlaceholder } from './data-rows';
import { LoadingDataGridDataPlaceholder } from './data-rows/loading-data-placeholder';
import { FooterRow } from './footer-row';
import { HeaderRowWrapper } from './header-row';
import { CenterHeaderColumns } from './header-row/center-header-columns';
import { FixedHeaderColumns } from './header-row/fixed-header-columns';
import { isHtmlElement } from './helpers';

const REQ_HEIGHT = 800;

export function useStickyScroll(ref: React.MutableRefObject<HTMLDivElement>): [string, number] {
	const [pos, setPos] = useState('relative');
	const [width, setWidthState] = useState(0);
	const setWidth = debounce(setWidthState, 1000);

	const CalcPos = () => {
		if (ref.current.getBoundingClientRect().bottom > window.innerHeight && ref.current.getBoundingClientRect().top < window.innerHeight - 30) {
			setPos('fixed');
		} else {
			setPos('relative');
		}
	};
	useResizeObserver(ref.current, c => {
		CalcPos();
		setWidth(c.contentRect.width);
	});
	React.useEffect(() => CalcPos(), []);

	const listener = (e: any) => {
		CalcPos();
	};

	useEffect(() => {
		window.addEventListener('scroll', listener);
		return () => {
			window.removeEventListener('scroll', listener);
		};
	}, []);

	return [pos, width];
}

export type VirtualPageHandler = (start: number, end: number) => void;

export type VirtualPaginationOptions = {
	limit?: number;
	handler: VirtualPageHandler;
};

/**
 * Specific styles that can be applied to this table. It's a complex component made up of sub-components,
 * so it made more sense to have a custom style object where the table can be targeted as a whole.
 */
export type TableStyleProps = {
	/**
	 * Border radius for the table header. Best used in conjunction with bodyBorderRadius.
	 */
	headerBorderRadius?: string;
	/**
	 * Colour of the table header.
	 */
	headerColour?: string;
	/**
	 * The rows are not a uniform thing and are made up of columns, so we colour the cells to get an impression of row colours.
	 * */
	cellsBgColour?: string;
	/**
	 * This is for alternating row colours. If you use these, do *NOT* use columnBgColour.
	 */
	alternateCellShades?: {
		primary: string;
		secondary: string;
	};
	/**
	 * Border radius value, applied to the whole table silhouette (as distinct from the headerBorderRadius, which sits beaneath it).
	 */
	bodyBorderRadius?: string;
};

export type DataGridProps<T> = Pick<FoxTableProps<T>, 'onColumnOrderChanged' | 'onColumnResized' | 'columns' | 'pagination' | 'data' | 'getRowKey'> & {
	total?: number;
	noDataPlaceHolder?: React.ReactNode;
	loadingData?: boolean;
	loadingDataMessage?: string;
	rowClassName?: string | ((row: T, rowIndex: number) => string | undefined);

	/** Styles we can apply to the table */
	tableStyle?: TableStyleProps;

	/** A handler that allows the data grid to update externally the pagination */
	virtualPage?: VirtualPaginationOptions;

	/** Exposes a function outside the datagrid to programatically set the scroll position to the row number */
	scrollToIndex?: (func: (index: number) => Promise<any>) => void;

	/**
	 * An explicit height to set for this grid, otherwise it will infer from the closest parent height context
	 */
	height?: number;
	maxHeight?: number;

	/**
	 * Optional param to set row height. Default - 40px
	 */
	rowHeight?: string;

	/**
	 * Optional param for hiding column borders. True for hide, undefined by default
	 */
	hideColumnBorders?: boolean;

	/** This is an extension component that will wrap the row and do something with the row data (e.g. wizard) */
	rowSelectionExtension?: (key: React.Key, record, children: React.ReactNode) => React.ReactNode;
};

const scrollbarWidth = () => {
	// thanks too https://davidwalsh.name/detect-scrollbar-width
	const scrollDiv = document.createElement('div');
	scrollDiv.setAttribute('style', 'width: 100px; height: 100px; overflow: scroll; position:absolute; top:-9999px;');
	document.body.appendChild(scrollDiv);
	const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
	document.body.removeChild(scrollDiv);
	return scrollbarWidth;
};

/** A hook that wraps useVirtual to enable fixed columns
 * @param fixLeft: an array of indices to be pinned, this should to be memoized
 */
function useFixedColumnVirtual({
	fixLeft,
	fixRight,
	sizes,
	...props
}: Omit<Options<HTMLDivElement>, 'rangeExtractor' | 'estimateSize'> & { fixLeft?: number[]; fixRight?: number; sizes: number[] }) {
	const id = useRef(nanoid());

	// allow space for the left fixed items
	const paddingStart = fixLeft?.length > 0 ? fixLeft.reduce((prev, idx) => prev + sizes[idx], 0) : 0;

	const leftSizes = (fixLeft ?? []).map(i => sizes[i]);

	const { virtualItems, ...virt } = useVirtual<HTMLDivElement>({
		...props,
		// all sizes are included so the virtual allows enough space
		// we don't use paddingStart / paddingEnd as we override the starts and that messes up the calculations
		// we could use dynamic calculations but it is easiest to just ignore these, given our use case is fixed column sizing
		estimateSize: useCallback(idx => sizes[idx], [sizes]),

		rangeExtractor: useCallback(
			r => {
				const range = [...fixLeft, ...defaultRangeExtractor(r).filter(r => !fixLeft.includes(r) && fixRight !== r), ...(fixRight !== undefined ? [fixRight] : [])];
				return range;
			},
			[fixLeft, fixRight]
		)
	});

	// This effect is to hook into scoll events and prevent the janky updates that occur if a full re-render happens. We want to update the transforms before the paint happens
	// which is fortunately nice and fast, allowing for buttery re-positioning of the elements. This relies on the given className being used for each element to quickly find them
	useEffect(() => {
		function scrollListener() {
			const spos = props.parentRef.current.scrollLeft;
			if (fixLeft) {
				let offset = spos;
				for (const x of fixLeft) {
					for (const col of Array.from(document.getElementsByClassName(`${id.current}_${x}`))) {
						if (isHtmlElement(col)) {
							col.style.transform = `translateX(${offset}px) ${col.style.transform.split(' ')[1]}`;
						}
					}
					offset += sizes[x];
				}
			}
			if (fixRight !== undefined) {
				let rightOffset = props.parentRef.current.clientWidth + spos;

				rightOffset -= sizes[fixRight];
				for (const col of Array.from(document.getElementsByClassName(`${id.current}_${fixRight}`))) {
					if (isHtmlElement(col)) {
						col.style.transform = `translateX(${rightOffset}px) ${col.style.transform.split(' ')[1]}`;
					}
				}
			}
		}

		if (props.parentRef.current) {
			props.parentRef.current.addEventListener('scroll', scrollListener);
		}
		return () => {
			if (props.parentRef.current) props.parentRef.current.removeEventListener('scroll', scrollListener);
		};
	}, [fixLeft, fixRight, sizes, props.parentRef]);

	const scrollPos = props.parentRef.current?.scrollLeft ?? 0;

	return {
		...virt,
		virtualItems: virtualItems.map(v => {
			// re-calculate the position of the column, based on index

			const fixedIndex = fixLeft?.indexOf(v.index);
			const start =
				fixedIndex >= 0 // this is one of the fixed left columns
					? leftSizes.slice(0, fixedIndex).reduce((p, c) => p + c, 0) + scrollPos
					: fixRight === v.index // the fixed right column (or not)
						? props.parentRef.current?.clientWidth - sizes[fixRight] + scrollPos
						: sizes // all other dynamically positioned columns
								.slice(0, v.index)
								.filter((_, idx) => !fixLeft.includes(idx) && fixRight !== idx)
								.reduce((p, c) => p + c, paddingStart);

			return {
				...v,
				start,
				zIndex: fixedIndex >= 0 ? 1 : 0,
				// helper to enable us to find and re-position the columns quickly
				columnClass: `${id.current}_${v.index}`
			};
		})
	};
}

const FULL_OVERFLOW: React.CSSProperties = { width: '100%', overflow: 'hidden' };
export const CELL_FOCUSED_CLASS = 'cell-item-focused';

const highlightedRow = ($tableStyle?: TableStyleProps) => css`
	${RowFiller} {
		background-color: ${props => ($tableStyle?.cellsBgColour ? alpha($tableStyle.cellsBgColour, 0.5) : props.theme.palette.action.hover)};
	}
	${Column} {
		background-color: ${props => ($tableStyle?.cellsBgColour ? alpha($tableStyle.cellsBgColour, 0.5) : props.theme.palette.action.hover)};
		&.fixed-left {
			background-color: ${props => ($tableStyle?.cellsBgColour ? alpha($tableStyle.cellsBgColour, 0.5) : props.theme.palette.grey[100])};
		}
		&.fixed-right {
			background-color: ${props => ($tableStyle?.cellsBgColour ? alpha($tableStyle.cellsBgColour, 0.5) : props.theme.palette.grey[100])};
		}
		${HoverRowVisible} {
			opacity: 1;
		}
		${Cell} {
			background-color: ${props => ($tableStyle?.cellsBgColour ? alpha($tableStyle.cellsBgColour, 0.5) : props.theme.palette.grey[100])};
		}
	}
`;

const RowWrapper = styled.div<{ $tableStyle?: TableStyleProps }>`
	&:hover,
	&:focus-within {
		${props => highlightedRow(props?.$tableStyle)}
	}
	// TODO: move pseudo-class to above rule once firefox supports ':has'
	// Duplication required or else Firefox will ignore entire rule!
	&:has(.${CELL_FOCUSED_CLASS}) {
		${props => highlightedRow(props?.$tableStyle)}
	}
`;

const HEADER_HEIGHT = 60;
const FOOTER_HEIGHT = 64;

/**
 *
 * @param props {DataGridProps}
 * @param props.onColumnResized - This requires the column's widths to be numbers, if using strings, such as "100px" the resize will not work
 *
 * Gotchas:
 * * Cannot have multiple fixed columns
 * * Column widths must be numbers to allow for resize
 * * Cannot be full width of container, as columns have fixed widths
 * * If you are seeing No Data flash on first render make sure you pass a loadingData: boolean prop. This will render the default loading spinner.
 */
function InnerDataGrid<T = Record<string, any>>({
	data,
	total,
	columns,
	getRowKey,
	rowClassName,
	virtualPage,
	noDataPlaceHolder,
	loadingData,
	scrollToIndex,
	height,
	maxHeight,
	rowHeight,
	hideColumnBorders,
	rowSelectionExtension,
	tableStyle
}: DataGridProps<T>) {
	const rows = data;

	const getContainerHeight = useContainerHeight();

	const scrollRef = React.useRef<HTMLDivElement>();
	const footerRef = React.useRef<HTMLDivElement>();
	const tableRootRef = React.useRef<HTMLDivElement>();

	const fullScreenContext = useFullScreenContext();

	const [reqHeight, setReqHeight] = useState(height ?? REQ_HEIGHT);
	const haveFooter = columns.some(column => column.columnFooter);

	useEffect(() => {
		if (height) {
			let baseHeight = height - HEADER_HEIGHT - 2;
			if (haveFooter) {
				baseHeight -= FOOTER_HEIGHT;
			}
			setReqHeight(baseHeight);
			return;
		}
		function winResize() {
			const containerHeight = getContainerHeight();
			let baseHeight = (containerHeight ? containerHeight : window.innerHeight) - HEADER_HEIGHT - 2;
			if (haveFooter) {
				baseHeight -= FOOTER_HEIGHT;
			}
			setReqHeight(baseHeight);
		}

		winResize();

		window.addEventListener('resize', winResize);
		return () => window.removeEventListener('resize', winResize);
	}, [fullScreenContext?.fullScreen, haveFooter, height]);

	const parentRef = React.useRef<HTMLDivElement>();

	const virtualPageDebounced = useCallback(
		debounce((start: number, end: number) => {
			virtualPage?.handler(start, end);
		}, 100),
		[virtualPage?.handler]
	);

	const rowVirtualizer = useVirtual({
		// if a virtual pagination handler is defined, we want to have the size be the total number of records, however we can optionally limit this (like the ES query)
		// without a handler it will always be the data's length
		size: virtualPage?.handler ? ((total > virtualPage?.limit ? virtualPage?.limit : total) ?? data?.length ?? 0) : data?.length,
		parentRef: parentRef,
		estimateSize: useCallback(() => 40, []),
		overscan: 1,
		rangeExtractor: useCallback(
			r => {
				virtualPageDebounced(r.start, r.end);
				return defaultRangeExtractor(r);
			},
			[virtualPageDebounced]
		)
	});

	const fixRight = useMemo(
		() =>
			columns
				.map((c, i) => ({ fixed: c.fixed, i }))
				.filter(c => c.fixed === 'right')
				.map(c => c.i)?.[0] ?? undefined,
		[columns]
	);

	const columnVirtualizer = useFixedColumnVirtual({
		parentRef: parentRef,
		horizontal: true,
		size: columns?.length ?? 0,
		sizes: useMemo(() => columns.map(a => a.width), [columns]),
		fixLeft: useMemo(
			() =>
				columns
					.map((c, i) => ({ fixed: c.fixed, i }))
					.filter(c => c.fixed === 'left')
					.map(c => c.i),
			[columns]
		),
		fixRight
	});
	const totalWidth = React.useMemo(() => columns.reduce((prev, c) => prev + Number(c.width), 0), [columns]);
	const totalWidthMinusRightFixed = fixRight ? totalWidth - columns[fixRight].width : totalWidth;

	const scrollToVirtualLastIndex = async (index: number) => {
		rowVirtualizer.scrollToIndex(index);
	};

	useEffect(() => {
		scrollToIndex?.(scrollToVirtualLastIndex);
	}, [scrollToIndex]);

	const sbWidth = useMemo(() => scrollbarWidth(), []);
	const totalSize = useDebounce(rowVirtualizer.totalSize, 100);

	const hasVertScroll = rowVirtualizer.totalSize > reqHeight;

	const fullScreenEnabled = fullScreenContext?.fullScreen === true;

	// why -1?
	// There is a bug where the horizontal scroll bar appears for specific browser widths / zoom when it shouldn't
	// -1 eliminates that bug.
	const fillerWidth = parentRef.current?.clientWidth - totalWidthMinusRightFixed - 1;

	useEffect(() => {
		if (fillerWidth > 0 && parentRef.current?.scrollLeft > 0) {
			parentRef.current.scrollTo(0, parentRef.current.scrollTop);
		}
	}, [fillerWidth]);

	const renderedData = rowVirtualizer.virtualItems.map(virtualRow => {
		const row = rows[virtualRow.index];
		const bgColour = calculateCellBgColours(tableStyle, virtualRow.index);
		const isLastRow = tableStyle && rows && rows.indexOf(row) === rows.length - 1;
		if (!row) {
			return (
				<Skeleton
					height={virtualRow.size}
					key={virtualRow.key}
					component={'div'}
					style={{
						left: 0,
						top: 0,
						height: virtualRow.size - 4, // the placeholder will appear to fill the space, so we just introduce a gap slightly
						width: totalWidthMinusRightFixed,
						position: 'absolute',
						transform: `translateX(0px) translateY(${virtualRow.start}px)`
					}}
				/>
			);
		}

		const rowKey = getRowKey?.(row, virtualRow.index) ?? `${virtualRow.key}${virtualRow.index}`;
		const rowContent = (
			<RowWrapper
				className={`root ${(typeof rowClassName === 'function' ? rowClassName(row, virtualRow.index) : typeof rowClassName === 'string' ? rowClassName : '') ?? ''}`}
				key={rowKey}
				$tableStyle={tableStyle}
			>
				{fillerWidth > 0 ? (
					<RowFiller
						style={{
							left: 0,
							top: 0,
							position: 'absolute',
							transform: `translateX(${totalWidthMinusRightFixed ?? 0}px) translateY(${virtualRow.start}px)`,
							width: fillerWidth,
							height: virtualRow.size
						}}
						$backgroundColour={bgColour}
						$isLastRow={isLastRow}
					></RowFiller>
				) : null}

				{columnVirtualizer.virtualItems.map(virtualColumn => {
					const column = columns?.[virtualColumn.index];
					if (column) {
						return (
							<Column
								key={`${column.dataIndex}${virtualRow.index}${column.columnKey ?? ''}`}
								id={`${column.dataIndex}|${virtualRow.index}`}
								className={`${virtualColumn.columnClass} ${column.fixed === 'left' ? 'fixed-left' : column.fixed === 'right' ? 'fixed-right' : 'center'}`}
								ref={el => {
									virtualRow.measureRef(el);
								}}
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									transform: `translateX(${virtualColumn.start}px) translateY(${virtualRow.start}px)`,
									zIndex: virtualColumn.zIndex
								}}
								$width={column.width}
								$hideColumnBorders={hideColumnBorders}
								$isLastRow={isLastRow}
							>
								{column.alwaysRerender ? (
									<CellItemPre
										columnCount={columns?.length}
										columnIndex={virtualColumn.index}
										columnType={column}
										record={row}
										rowHeight={rowHeight}
										rowIndex={virtualRow.index}
										tableStyle={tableStyle}
									/>
								) : (
									<CellItem
										columnCount={columns?.length}
										columnIndex={virtualColumn.index}
										columnType={column}
										record={row}
										rowHeight={rowHeight}
										rowIndex={virtualRow.index}
										tableStyle={tableStyle}
									/>
								)}
							</Column>
						);
					}
				})}
			</RowWrapper>
		);
		return rowSelectionExtension ? rowSelectionExtension(rowKey, row, rowContent) : rowContent;
	});

	const innerCalcHeight = Math.max((scrollRef.current?.clientWidth < totalWidth ? sbWidth : 0) + totalSize, loadingData || !rows?.length ? 100 : 0);

	return (
		<TableWrapper key="tableWrapper" className="dataTableRoot" ref={tableRootRef} $borderRadius={tableStyle?.bodyBorderRadius}>
			<HeaderRowWrapper ref={scrollRef} $backgroundColor={tableStyle?.headerColour} $borderRadius={tableStyle?.headerBorderRadius}>
				<FixedHeaderColumns position="left" hideColumnBorders={hideColumnBorders} tableStyle={tableStyle} />
				<CenterHeaderColumns hasVertScroll={hasVertScroll} hideColumnBorders={hideColumnBorders} tableStyle={tableStyle} />
				<FixedHeaderColumns position="right" hideColumnBorders={hideColumnBorders} tableStyle={tableStyle} />
			</HeaderRowWrapper>
			<div
				ref={parentRef}
				style={{
					height: fullScreenEnabled ? reqHeight : maxHeight ? Math.min(maxHeight, innerCalcHeight) : hasVertScroll ? reqHeight : innerCalcHeight,
					overflowY: hasVertScroll ? 'auto' : 'hidden',
					overflowX: fillerWidth - (fixRight ? columns[fixRight].width : 0) < 0 ? 'auto' : 'hidden',
					width: '100%',
					position: 'relative',
					borderRadius: tableStyle?.bodyBorderRadius ? `0 0 ${tableStyle.bodyBorderRadius} ${tableStyle.bodyBorderRadius}` : undefined
				}}
				onScroll={e => {
					scrollRef.current?.scroll({
						left: e.currentTarget.scrollLeft,
						behavior: 'auto'
					});
					footerRef.current?.scroll({
						left: e.currentTarget.scrollLeft,
						behavior: 'auto'
					});
				}}
			>
				<div style={{ height: rowVirtualizer.totalSize, width: columnVirtualizer.totalSize, position: 'relative' }}>
					{loadingData ? (
						<LoadingDataGridDataPlaceholder />
					) : !rows?.length ? (
						noDataPlaceHolder ? (
							<RowPlaceholder>{noDataPlaceHolder}</RowPlaceholder>
						) : (
							<RowPlaceholder>
								<NoData message="No Data" />
							</RowPlaceholder>
						)
					) : null}
					{renderedData}
				</div>
			</div>
			{loadingData ? null : (
				<div ref={footerRef} style={FULL_OVERFLOW}>
					<FooterRow key={`footer`} totalWidth={totalWidth + sbWidth} />
				</div>
			)}
		</TableWrapper>
	);
}

// In the dashboard widget version, the border doubles-up on an existing border.
// At the moment if a border radius is being passed in, it's a custom style, so we don't want the doubling-up of borders.
const TableWrapper = styled.div<{ $borderRadius?: string }>`
	${props => (!props.$borderRadius ? `border: 1px solid ${props.theme.palette.grey[300]};` : '')};
	border-radius: ${props => props.$borderRadius ?? ''};
	width: 100%; //max-content;
	display: grid;
	max-width: 100%;
	.dataTableRoot {
		.row {
			opacity: 1;
		}
	}
`;

export function DataGrid<T = Record<string, any>>({
	onColumnOrderChanged,
	onColumnResized,
	columns,
	data,
	getRowKey,
	noDataPlaceHolder,
	loadingData,
	loadingDataMessage,
	scrollToIndex,
	rowHeight,
	hideColumnBorders,
	tableStyle,
	...rest
}: DataGridProps<T>) {
	return (
		<DataGridContextProvider
			value={{
				onColumnOrderChanged,
				onColumnResized,
				columns,
				getRowKey,
				noDataPlaceHolder,
				loadingData,
				loadingDataMessage
			}}
		>
			<DataGridDataContextProvider value={data}>
				<InnerDataGrid
					{...rest}
					data={data}
					columns={columns}
					onColumnOrderChanged={onColumnOrderChanged}
					onColumnResized={onColumnResized}
					getRowKey={getRowKey}
					noDataPlaceHolder={noDataPlaceHolder}
					loadingData={loadingData}
					loadingDataMessage={loadingDataMessage}
					scrollToIndex={scrollToIndex}
					rowHeight={rowHeight}
					hideColumnBorders={hideColumnBorders}
					tableStyle={tableStyle}
				/>
			</DataGridDataContextProvider>
		</DataGridContextProvider>
	);
}
